import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import {
  useListWorkflowDefinitionsQuery,
  WorkflowStatus,
  WorkflowDefinitionSummary,
} from '@/redux/internal-services/workflowApi';

const STATUS_BADGE: Record<WorkflowStatus, string> = {
  draft:     'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  published: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  archived:  'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const STATUSES: Array<{ label: string; value: WorkflowStatus | '' }> = [
  { label: 'All', value: '' },
  { label: 'Draft', value: 'draft' },
  { label: 'Published', value: 'published' },
  { label: 'Archived', value: 'archived' },
];

const StatusBadge: React.FC<{ status: WorkflowStatus }> = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[status]}`}>
    {status}
  </span>
);

const MetaPill: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-ui-subtle text-ui-text2">
    <span className="font-medium">{value}</span>
    <span>{label}</span>
  </span>
);

const WorkflowsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<WorkflowStatus | ''>('');

  const { data, isLoading, isFetching } = useListWorkflowDefinitionsQuery(
    { status: statusFilter || undefined, limit: 100 },
    { refetchOnMountOrArgChange: true },
  );

  const definitions: WorkflowDefinitionSummary[] = data?.data ?? [];
  const total = data?.total ?? 0;

  const draftCount     = definitions.filter((d) => d.status === 'draft').length;
  const publishedCount = definitions.filter((d) => d.status === 'published').length;
  const archivedCount  = definitions.filter((d) => d.status === 'archived').length;

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-ui-text">Workflow Definitions</h1>
              <p className="text-sm text-ui-text2 mt-1">
                Manage platform-wide workflow definitions. Published definitions can be adopted by tenants.
              </p>
            </div>
            <button
              onClick={() => navigate('/platform/workflows/new')}
              className="shrink-0 px-4 py-2 bg-brand-dark text-white text-sm font-medium rounded-lg hover:opacity-90 transition"
            >
              + New Workflow
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUSES.map(({ label, value }) => (
              <button
                key={label}
                onClick={() => setStatusFilter(value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  statusFilter === value
                    ? 'bg-brand-dark text-white'
                    : 'bg-ui-subtle text-ui-text2 hover:bg-ui-hover'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Definitions', value: total,          color: 'text-brand-dark' },
          { label: 'Draft',             value: draftCount,     color: 'text-yellow-700 dark:text-yellow-400' },
          { label: 'Published',         value: publishedCount, color: 'text-green-700 dark:text-green-400' },
          { label: 'Archived',          value: archivedCount,  color: 'text-ui-text3' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-ui-card border border-ui-border2 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-medium text-ui-text2 mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{isLoading ? '—' : value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-ui-card border border-ui-border2 rounded-xl shadow-sm overflow-hidden">
        {isLoading || isFetching ? (
          <div className="py-20 text-center text-ui-text3 text-sm">Loading workflow definitions...</div>
        ) : definitions.length === 0 ? (
          <div className="py-20 text-center">
            <div className="text-5xl mb-4">⚙️</div>
            <p className="text-ui-text3 text-sm">{t('platform.noWorkflowDefinitions')}</p>
            <p className="text-ui-text3 text-xs mt-1">{t('platform.createFirstDefinition')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ui-border bg-ui-raised">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Definition</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider hidden md:table-cell">Structure</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider hidden lg:table-cell">Engagement Types</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider hidden lg:table-cell">Updated</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ui-border">
                {definitions.map((def) => (
                  <tr
                    key={`${def.definition_id}@${def.version}`}
                    className="hover:bg-ui-hover transition cursor-pointer"
                    onClick={() => navigate(`/platform/workflows/${def.definition_id}/${def.version}`)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-ui-text">{def.name}</p>
                        <p className="text-xs text-ui-text3 mt-0.5 font-mono">{def.definition_id} v{def.version}</p>
                        {def.description && (
                          <p className="text-xs text-ui-text3 mt-0.5 line-clamp-1">{def.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={def.status} />
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        <MetaPill label="stages"  value={def.stage_count} />
                        <MetaPill label="statuses" value={def.status_count} />
                        <MetaPill label="transitions" value={def.transition_count} />
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {def.engagement_types.length === 0 ? (
                          <span className="text-xs text-ui-text3">—</span>
                        ) : (
                          def.engagement_types.map((et) => (
                            <span key={et} className="px-2 py-0.5 rounded text-xs bg-ui-subtle text-ui-text2 capitalize">
                              {et.replace(/_/g, ' ')}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <span className="text-sm text-ui-text3">
                        {def.updated_on ? new Date(def.updated_on).toLocaleDateString() : '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/platform/workflows/${def.definition_id}/${def.version}`);
                        }}
                        className="text-xs text-brand-dark font-medium hover:underline"
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default WorkflowsPage;
